import { useEffect, useRef } from 'react'
import { ExternalLink, Check } from 'lucide-react'
import SectionHeading from './SectionHeading'
import { PROJECT_AZURE_ZERO_TRUST } from '../data/projects'
import { initGSAP, prefersReducedMotion } from '../lib/motion'

const STORY: { num: string; title: string; body: string }[] = [
  {
    num: '01',
    title: 'The problem',
    body: PROJECT_AZURE_ZERO_TRUST.problem,
  },
  {
    num: '02',
    title: 'Why it is hard',
    body: PROJECT_AZURE_ZERO_TRUST.whyHard,
  },
  {
    num: '03',
    title: 'The solution',
    body: PROJECT_AZURE_ZERO_TRUST.solution,
  },
]

const RING_ITEMS = PROJECT_AZURE_ZERO_TRUST.flow.filter((s) =>
  ['Workload Identity', 'GitHub Actions OIDC', 'Argo CD', 'Security ring'].includes(
    s.label
  )
)

const AzureCaseStudy: React.FC<{ standalone?: boolean }> = ({
  standalone = false,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        '[data-azure]',
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: {
            trigger: ref.current,
            start: standalone ? 'top 92%' : 'top 75%',
            once: true,
          },
        }
      )
    }, ref)
    return () => ctx.revert()
  }, [standalone])

  return (
    <section
      id={standalone ? undefined : 'azure'}
      ref={ref}
      className={`${
        standalone ? 'pt-8 pb-20 sm:pb-28' : 'section-padding'
      } bg-neutral-50 dark:bg-neutral-900/30`}
    >
      <div className="container-width">
        <div data-azure>
          <SectionHeading
            label="Case study"
            meta="02 · zero-trust network"
            title={PROJECT_AZURE_ZERO_TRUST.title}
            description={PROJECT_AZURE_ZERO_TRUST.tagline}
          />
        </div>

        <div data-azure>
          <div className="flex items-center gap-3 mb-8 flex-wrap">
            <span className="mono-label !text-primary-500">
              {PROJECT_AZURE_ZERO_TRUST.kind}
            </span>
            <span className="hidden sm:block w-px h-4 bg-neutral-300 dark:bg-white/15" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">
              {PROJECT_AZURE_ZERO_TRUST.role}
            </span>
            <a
              href={PROJECT_AZURE_ZERO_TRUST.repo}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-2 btn-secondary !py-2 !px-4 text-sm"
            >
              View repository
              <ExternalLink size={14} />
            </a>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {STORY.map((block) => (
              <div
                key={block.num}
                className="rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 p-6"
              >
                <span className="font-mono text-xs text-primary-500">
                  {block.num}
                </span>
                <h3 className="font-display text-lg font-semibold tracking-tight mt-2 mb-3">
                  {block.title}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  {block.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div data-azure className="mt-14">
          <h3 className="font-display text-xl font-semibold tracking-tight mb-6 flex items-center gap-3">
            Network topology
            <span className="mono-label !text-xs !text-neutral-400 dark:!text-neutral-500">
              hub-and-spoke
            </span>
          </h3>

          <div className="diagram-shell p-5 sm:p-8">
            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-stretch">
              <div className="arch-node arch-node-accent flex flex-col justify-center text-left">
                <span className="arch-node-title">Hub</span>
                <span className="arch-node-sub">
                  Azure Firewall · shared services · centralized egress and
                  inspection
                </span>
              </div>

              <div className="hidden md:flex flex-col items-center justify-center gap-2 px-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
                  peered
                </span>
                <div className="flex items-center gap-1 text-primary-500/70">
                  <span className="block w-10 h-px bg-primary-500/50" />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                  <span className="block w-10 h-px bg-primary-500/50" />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
                  inspected
                </span>
              </div>

              <div className="arch-node flex flex-col justify-center text-left">
                <span className="arch-node-title">Spoke</span>
                <span className="arch-node-sub">
                  Private AKS — no public control plane, no public workload
                  endpoints
                </span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-white/6">
              <span className="mono-label !text-xs !text-neutral-400 dark:!text-neutral-500 mb-4 block">
                Security & delivery ring
              </span>
              <div className="grid gap-3 sm:grid-cols-2">
                {RING_ITEMS.map((item) => (
                  <div key={item.label} className="arch-ring-item">
                    <span className="arch-ring-icon">
                      <Check size={11} />
                    </span>
                    <span>
                      <span className="font-medium text-neutral-800 dark:text-neutral-100">
                        {item.label}
                      </span>
                      {item.sub && (
                        <span className="block text-[11px] text-neutral-500 dark:text-neutral-400">
                          {item.sub}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div data-azure className="mt-14 grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-4">
              The platform
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
              The controls that make a private cluster stay private — and
              observable while it does.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {PROJECT_AZURE_ZERO_TRUST.platform.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-xl border border-neutral-200 dark:border-white/8 px-4 py-3 text-sm text-neutral-700 dark:text-neutral-200"
              >
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div data-azure className="mt-14">
          <h3 className="font-display text-xl font-semibold tracking-tight mb-6 flex items-center gap-3">
            Key decisions
            <span className="mono-label !text-xs !text-neutral-400 dark:!text-neutral-500">
              trade-offs
            </span>
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROJECT_AZURE_ZERO_TRUST.challenges.map((c, i) => (
              <div
                key={c.title}
                className="group rounded-2xl border border-neutral-200 dark:border-white/10 p-5 transition-colors duration-200 hover:border-primary-500/30"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-mono text-[11px] text-neutral-400 dark:text-neutral-500">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h4 className="font-display text-base font-semibold tracking-tight">
                    {c.title}
                  </h4>
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  {c.decision}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div
          data-azure
          className="mt-14 rounded-3xl bg-neutral-950 dark:bg-neutral-900 border border-neutral-800 dark:border-white/10 p-8 sm:p-12 text-white"
        >
          <span className="mono-label !text-primary-400">Outcomes</span>
          <h3 className="font-display text-2xl font-semibold tracking-tight mt-3 mb-8">
            What shipping it actually changed
          </h3>
          <ul className="grid gap-4 sm:grid-cols-2">
            {PROJECT_AZURE_ZERO_TRUST.results.map((result) => (
              <li
                key={result}
                className="flex items-start gap-3 text-sm text-neutral-300 leading-relaxed"
              >
                <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-md bg-primary-500/20 text-primary-400 flex-shrink-0">
                  <Check size={13} />
                </span>
                {result}
              </li>
            ))}
          </ul>
        </div>

        <div data-azure className="mt-10 flex flex-wrap gap-2">
          {PROJECT_AZURE_ZERO_TRUST.technologies.map((tech) => (
            <span
              key={tech}
              className="inline-flex items-center rounded-lg border border-neutral-200 dark:border-white/8 px-3 py-1.5 font-mono text-xs text-neutral-600 dark:text-neutral-300"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

export default AzureCaseStudy